import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import useAuth from './useAuth';
import { logger } from '../utils/logger';

const CustomLoginCallback: React.FC = () => {
  const { oktaAuth } = useAuth();
  const navigate = useNavigate();
  const [hasProcessed, setHasProcessed] = React.useState(false);

  useEffect(() => {
    // Prevent double processing
    if (hasProcessed) {
      logger.debug('Callback already processed, skipping...');
      return;
    }

    const handleCallback = async () => {
      try {
        logger.info('Starting login callback handling...');
        setHasProcessed(true);

        // Handle the callback from Okta
        await oktaAuth.handleLoginRedirect();

        logger.debug('Login redirect handled, getting auth state...');

        // Get the auth state and wait for it to be ready
        const authState = await oktaAuth.authStateManager.getAuthState();

        logger.debug('Auth state received', {
          isAuthenticated: authState?.isAuthenticated,
          hasIdToken: !!authState?.idToken,
          hasAccessToken: !!authState?.accessToken
        });

        if (authState && authState.isAuthenticated) {
          logger.info('User authenticated successfully by Okta');

          // Clear any previous auth errors since authentication succeeded
          sessionStorage.removeItem('auth_error');
          sessionStorage.removeItem('returning_from_unauthorized');

          // Set flag to indicate successful authentication in progress
          sessionStorage.setItem('auth_in_progress', 'true');

          // User is authenticated and authorized by Okta, proceed to the app
          const email = authState.idToken?.claims?.email as string;
          logger.info('Authenticated user', { email });

          // Get original URI or default to home
          const originalUri = oktaAuth.getOriginalUri();
          logger.debug('Original URI from Okta', { originalUri });

          // Small delay to ensure auth state is fully propagated
          await new Promise(resolve => setTimeout(resolve, 100));

          // Navigate to original URI or home
          const targetUri = (originalUri && originalUri !== '/') ? originalUri : '/';
          logger.info('Navigating to target', { targetUri });

          navigate(targetUri, { replace: true });
        } else {
          // Not authenticated after callback - redirect to login
          logger.warn('Authentication state not available after callback');
          navigate('/login');
        }
      } catch (error: unknown) {
        const err = error as Record<string, unknown>;
        logger.error('Login callback error', err, {
          errorCode: err.errorCode,
          name: err.name
        });

        // Check for specific Okta access denied errors
        if (err.errorCode === 'access_denied') {
          logger.warn('Access denied by Okta');
          sessionStorage.setItem('auth_error', 'access_denied');
          navigate('/unauthorized');
        } else if (err.errorCode === 'login_required') {
          logger.warn('Login required');
          navigate('/login');
        } else if ((err.message as string)?.toLowerCase().includes('unauthorized')) {
          logger.warn('Unauthorized error detected');
          sessionStorage.setItem('auth_error', 'unauthorized');
          navigate('/unauthorized');
        } else {
          logger.error('Unexpected authentication error, redirecting to login');
          navigate('/login');
        }
      }
    };

    handleCallback();
  }, [oktaAuth, navigate, hasProcessed]);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh'
    }}>
      <Spin size="large" tip="Processing login..." />
    </div>
  );
};

export default CustomLoginCallback;
